/**
 * Shared helpers for cost/sell/margin calculations used across Stock tabs.
 *
 * WAC  = Weighted Average Cost from purchases (90-day rolling, keyed by catalog_id)
 * WASP = Weighted Average Selling Price from sales (keyed by catalog_id)
 *
 * Fallback chain:
 *   effectiveCost: wacMap[catalog_id] → product.cost_price
 *   effectiveSell: waspMap[catalog_id] → product.mrp
 */

/**
 * Returns the best available cost for a product.
 * @param {object} p       - Product row from the API (must have catalog_id)
 * @param {object} wacMap  - { [catalog_id]: wac_float }
 */
export const effectiveCost = (p, wacMap = {}) => {
  const byId = wacMap[p.catalog_id];
  if (byId != null) return byId;
  const nameKey = (p.name || p.product_name || '').trim().toLowerCase();
  if (nameKey && wacMap[nameKey] != null) return wacMap[nameKey];
  return (p.cost_price != null && p.cost_price > 0) ? p.cost_price : null;
};

/**
 * Returns the best available selling price for a product.
 * @param {object} p        - Product row
 * @param {object} waspMap  - { [catalog_id]: wasp_float }
 */
export const effectiveSell = (p, waspMap = {}) => {
  const byId = waspMap[p.catalog_id];
  if (byId != null) return byId;
  return p.mrp != null ? p.mrp : null;
};

/**
 * Margin percentage: ((sell - cost) / cost) * 100
 * Returns null if cost is 0 or either value is missing.
 */
export const marginPct = (cost, sell) => {
  if (cost == null || sell == null || cost <= 0) return null;
  return ((sell - cost) / cost) * 100;
};
